/* eslint-disable @typescript-eslint/no-floating-promises */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import useSportsbookBetslip from './use-sportsbook-betslip';
import { selectIsReadyForApiCalls } from '../sportsbook-root-selectors';
import * as Api from '../api/sportsbook-api';
import { ROUTE_SPORTSBOOK_FLAT_EVENTS } from '../../../router/constants';
import reportSportsbookError from '../utils/sportsbook-error-reporter';

const QUERY_PARAM_REFERENCE_IDS = 'add-bet-by-reference';
const QUERY_PARAM_PROVIDER_ID = 'provider-id';
const QUERY_PARAM_GO_TO = 'go-to';

async function getEventAndSelectionIdsFromReferenceIds(
	referenceIds: Array<string>,
	providerId: string,
	redirectReference: string | null
) {
	if (referenceIds.length === 0) {
		return { eventId: null, selections: [] };
	}

	try {
		const selections = await Api.fetchSelectionsByReference(referenceIds, providerId);

		if (selections.length === 0) {
			return { eventId: null, selections: [] };
		}

		const redirectEventId = selections.find(selection => selection.externalEventId === redirectReference)?.eventId;

		return {
			eventId: redirectEventId,
			selections
		};
	} catch (error) {
		reportSportsbookError(
			`Failed to fetch selections by reference for referenceIds=${referenceIds.join(',')}`,
			error,
			{ providerId }
		);
		return { eventId: null, selections: [] };
	}
}

export default function useConsumeQuerySelectionsByReference() {
	const router = useRouter();
	const location = useLocation();
	const { addSelectionsToBetslip } = useSportsbookBetslip();
	const isApiReady = useSelector(selectIsReadyForApiCalls);
	const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	// Reduce the reference ids because we want to support both formats: ?add-bet-by-reference=123&add-bet-by-reference=789 and comma separated ?add-bet-by-reference=123,456,789
	const referenceIds = queryParams
		.getAll(QUERY_PARAM_REFERENCE_IDS)
		.reduce<string[]>((acc, referenceId) => {
			const splitReferenceIds = referenceId.split(',');
			return [...acc, ...splitReferenceIds];
		}, [])
		.filter((value, index, self) => self.indexOf(value) === index);
	const providerId = queryParams.get(QUERY_PARAM_PROVIDER_ID);
	const redirectReference = queryParams.get(QUERY_PARAM_GO_TO);
	const [isLoading, setIsLoading] = useState(false);

	const handleAddSelectionsByReference = useCallback(async () => {
		if (referenceIds.length > 0 && providerId && isApiReady && !isLoading) {
			setIsLoading(true);
			const { eventId, selections } = await getEventAndSelectionIdsFromReferenceIds(
				referenceIds,
				providerId,
				redirectReference
			);

			addSelectionsToBetslip(selections);

			if (eventId) {
				router.push(`${ROUTE_SPORTSBOOK_FLAT_EVENTS}/${eventId}`);
			}

			queryParams.delete(QUERY_PARAM_REFERENCE_IDS);
			queryParams.delete(QUERY_PARAM_PROVIDER_ID);
			queryParams.delete(QUERY_PARAM_GO_TO);
			setIsLoading(false);
		}
	}, [
		router,
		addSelectionsToBetslip,
		isApiReady,
		referenceIds,
		providerId,
		redirectReference,
		queryParams,
		isLoading
	]);

	useEffect(() => {
		handleAddSelectionsByReference();
	}, [handleAddSelectionsByReference]);
}
